/**
 * Public listen page — what a label lands on when they click a demo email's
 * tracked short URL. No KNKT account required.
 *
 *   knkt-links short URL → 302 → /listen/<audioFileId>?via=<slug>
 *
 * Renders a stripped-down player and ships play/progress/pause/ended
 * events to pitch's /api/listen-events with the `via` slug, so the
 * pitching artist sees engagement in their /history dashboard.
 *
 * Why this and not the existing AnalyzePage / ResultsPage:
 *   - those pages are gated by AuthContext (labels don't have KNKT logins)
 *   - they expose features (re-analyze, override, etc.) the label has no
 *     business seeing
 *   - a focused listen surface is faster to render and easier to embed
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Play, Pause, Music, SkipBack, SkipForward, Volume2, VolumeX, Heart, ThumbsUp, ThumbsDown, MessageSquare, Send, Lock, ArrowUpRight, Share2, Check, Download } from 'lucide-react';
import './ListenPage.css';

const PITCH_INGEST = import.meta.env.VITE_PITCH_API_URL || 'https://pitch.knkt.io';
const PROGRESS_PING_MS = 5000;

/** Where the "Sign in" CTA in the top bar lands. The KNKT platform login
 *  is the canonical entrypoint — drops them into knkt.io and they can pick
 *  whichever sub-product is relevant from there. */
const KNKT_LOGIN_URL = (import.meta.env.VITE_KNKT_LOGIN_URL as string | undefined) || 'https://knkt.io/login';

function TopHeader() {
  return (
    <header className="listen-top-header" aria-label="KNKT Listen">
      <a
        className="listen-top-brand"
        href={KNKT_LOGIN_URL}
        target="_blank"
        rel="noreferrer"
        aria-label="KNKT Listen — sign in"
      >
        <img src="/knkt-logo.svg" alt="KNKT" className="listen-top-mark" />
        <span className="listen-top-product">Listen</span>
      </a>
      <a
        className="listen-top-cta"
        href={KNKT_LOGIN_URL}
        target="_blank"
        rel="noreferrer"
      >
        Sign in
        <ArrowUpRight size={12} strokeWidth={2.4} />
      </a>
    </header>
  );
}

function Watermark() {
  return (
    <div className="listen-watermark" aria-hidden="true">
      <img src="/knkt-logo.svg" alt="" className="listen-watermark-mark" />
    </div>
  );
}

function Copyright() {
  return (
    <section className="listen-copyright" aria-label="Copyright notice">
      <div className="listen-copyright-head">
        <Lock size={11} strokeWidth={2.2} />
        <span>For the intended recipient only</span>
      </div>
      <p className="listen-copyright-text">
        This is an <em>unreleased</em> work shared privately with you via KNKT
        Pitch. © All rights reserved by the artist and KNKT. Do not redistribute,
        forward, publish, or upload to streaming platforms.
      </p>
    </section>
  );
}

interface ListenMeta {
  audioFileId: string;
  filename: string;
  durationSeconds: number | null;
  format: string | null;
  fileUrl: string;
  /** Same S3 object but signed with Content-Disposition: attachment so the
   *  browser saves instead of streaming inline. Older API responses won't
   *  include this — fall back to fileUrl (which will play instead of
   *  download, but at least the click still works). */
  downloadUrl?: string;
}

/** Random per-visit id so multiple sessions don't collapse into one row. */
function makeSessionId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

export default function ListenPage() {
  const { audioFileId } = useParams<{ audioFileId: string }>();
  const [search] = useSearchParams();
  const via = search.get('via');           // knkt-links slug

  const sessionId = useMemo(makeSessionId, []);
  const [meta, setMeta] = useState<ListenMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const lastPingAt = useRef<number>(0);
  const lastPosAt = useRef<number>(0);

  // Fetch metadata + signed URL.
  // Relative URL on purpose — both audioagent.knkt.io AND the neutral
  // listen.knkt.io alias point at the same CloudFront distro, so /api/...
  // works from either origin without a CORS roundtrip.
  useEffect(() => {
    if (!audioFileId) return;
    let cancelled = false;
    fetch(`/api/listen/${encodeURIComponent(audioFileId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (!cancelled) setMeta(d); })
      .catch(() => { if (!cancelled) setError('Track unavailable'); });
    return () => { cancelled = true; };
  }, [audioFileId]);

  /**
   * Best-effort fire — never blocks playback, never throws.
   * delta_seconds = time played since the previous event, used to sum
   * total_seconds server-side. Reset on play/seek.
   */
  function emit(type: 'play' | 'pause' | 'progress' | 'ended' | 'download', positionSeconds: number, deltaSeconds = 0) {
    if (!audioFileId) return;
    const body = {
      type,
      slug: via,
      audio_file_id: audioFileId,
      session_id: sessionId,
      position_seconds: Math.max(0, Math.floor(positionSeconds)),
      duration_seconds: duration > 0 ? Math.floor(duration) : undefined,
      delta_seconds: deltaSeconds > 0 ? Math.floor(deltaSeconds) : undefined,
    };
    fetch(`${PITCH_INGEST}/api/listen-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  }

  const onPlay = () => {
    setIsPlaying(true);
    const pos = audioRef.current?.currentTime ?? 0;
    lastPingAt.current = Date.now();
    lastPosAt.current = pos;
    emit('play', pos);
  };

  const onPause = () => {
    setIsPlaying(false);
    const pos = audioRef.current?.currentTime ?? 0;
    const delta = Math.max(0, pos - lastPosAt.current);
    emit('pause', pos, delta);
    lastPosAt.current = pos;
  };

  const onEnded = () => {
    setIsPlaying(false);
    const pos = audioRef.current?.currentTime ?? 0;
    const delta = Math.max(0, pos - lastPosAt.current);
    emit('ended', pos, delta);
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime);
    // Ping every PROGRESS_PING_MS during playback only.
    if (!isPlaying) return;
    const now = Date.now();
    if (now - lastPingAt.current >= PROGRESS_PING_MS) {
      const delta = Math.max(0, audio.currentTime - lastPosAt.current);
      emit('progress', audio.currentTime, delta);
      lastPingAt.current = now;
      lastPosAt.current = audio.currentTime;
    }
  };

  const onLoadedMetadata = () => {
    if (audioRef.current?.duration && isFinite(audioRef.current.duration)) {
      setDuration(audioRef.current.duration);
    }
  };

  // Emit a final progress when the user closes the tab while playing.
  useEffect(() => {
    function handleUnload() {
      const audio = audioRef.current;
      if (!audio || !isPlaying) return;
      const delta = Math.max(0, audio.currentTime - lastPosAt.current);
      emit('pause', audio.currentTime, delta);
    }
    window.addEventListener('pagehide', handleUnload);
    return () => window.removeEventListener('pagehide', handleUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);

  // Push volume + muted changes into the <audio> element.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.muted = muted;
  }, [volume, muted]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => { /* user gesture required */ });
    else audio.pause();
  };

  /** Skip relative to current time, clamped to [0, duration]. */
  const skip = (deltaSec: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const next = Math.max(0, Math.min(duration, audio.currentTime + deltaSec));
    // Reset our progress baseline so the next event records the right delta.
    lastPosAt.current = next;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  /** Seek on click anywhere on the progress bar — natural scrubbing. */
  const seekFromEvent = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const next = ratio * duration;
    lastPosAt.current = next;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const toggleMute = () => setMuted((m) => !m);

  // Reactions — anonymous, identified by (slug, session_id). Toggle state
  // lives client-side; the API is idempotent so retries are safe.
  type Reaction = 'love' | 'thumbs_up' | 'thumbs_down';
  const [reactions, setReactions] = useState<{ [K in Reaction]?: boolean }>({});
  const [comment, setComment] = useState('');
  const [commentSent, setCommentSent] = useState(false);
  const [commentSending, setCommentSending] = useState(false);

  function postReaction(method: 'POST' | 'DELETE', type: Reaction | 'comment', extra?: { comment?: string }) {
    if (!audioFileId) return Promise.resolve();
    return fetch(`${PITCH_INGEST}/api/listen-reactions`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        slug: via,
        audio_file_id: audioFileId,
        session_id: sessionId,
        comment: extra?.comment,
      }),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  }

  const toggleReaction = (type: Reaction) => {
    setReactions((cur) => {
      const next = { ...cur };
      const wasOn = Boolean(cur[type]);
      // Thumbs are mutually exclusive — turning one on turns the other off.
      if (type === 'thumbs_up'   && !wasOn) next.thumbs_down = false;
      if (type === 'thumbs_down' && !wasOn) next.thumbs_up   = false;
      next[type] = !wasOn;
      // Fire side-effects after computing local state so the UI feels snappy.
      postReaction(wasOn ? 'DELETE' : 'POST', type);
      if (type === 'thumbs_up'   && !wasOn && cur.thumbs_down) postReaction('DELETE', 'thumbs_down');
      if (type === 'thumbs_down' && !wasOn && cur.thumbs_up)   postReaction('DELETE', 'thumbs_up');
      return next;
    });
  };

  /**
   * Build the cleanest possible share URL. When the recipient landed here
   * from a knkt-links short link (the common case — `via` is set), we hand
   * back the *same* link.knkt.io/<slug> they could've shared in the first
   * place. That way:
   *   - it stays 17 chars long instead of the 80+ char listen.knkt.io URL
   *   - forwarded clicks still attribute back to the original artist
   *     (knkt-links 302s here with the same via= attached)
   * No via? We rewrite to the neutral listen.knkt.io/<audioFileId> alias
   * regardless of which host actually served this page — the old
   * audioagent.knkt.io/listen/<id> form is hostile to forward.
   */
  const shareUrl = useMemo(() => {
    if (via) return `https://link.knkt.io/${via}`;
    if (audioFileId) return `https://listen.knkt.io/${audioFileId}`;
    return typeof window !== 'undefined' ? window.location.href : '';
  }, [via, audioFileId]);

  const [shareCopied, setShareCopied] = useState(false);
  const shareResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyShareUrl = async () => {
    const flash = () => {
      setShareCopied(true);
      if (shareResetTimer.current) clearTimeout(shareResetTimer.current);
      shareResetTimer.current = setTimeout(() => setShareCopied(false), 1800);
    };
    try {
      await navigator.clipboard.writeText(shareUrl);
      flash();
    } catch {
      // Fallback for http origins or older browsers.
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        flash();
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  // Strip the scheme for display — looks cleaner in the chip and is what
  // people copy/paste verbatim anyway. Keep the protocol in shareUrl for
  // the actual clipboard write.
  const shareUrlDisplay = shareUrl.replace(/^https?:\/\//, '');

  // Download — DJs almost always pull a copy to test in their decks before
  // signing. We fire a `download` event before triggering the browser
  // download so the artist sees the count in their dashboard. Best-effort
  // tracking: if the event fails the download still happens.
  const [downloadFlash, setDownloadFlash] = useState(false);
  const downloadFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleDownload = () => {
    const url = meta?.downloadUrl || meta?.fileUrl;
    if (!url) return;
    const pos = audioRef.current?.currentTime ?? 0;
    emit('download', pos);
    // The signed URL is cross-origin (S3 vs audioagent.knkt.io), so the
    // anchor's `download` attribute alone is ignored — the browser would
    // stream the file in a new tab. The backend mints `downloadUrl` with
    // `ResponseContentDisposition: attachment`, which makes S3 send the
    // file with a Content-Disposition header and the browser saves it.
    const a = document.createElement('a');
    a.href = url;
    a.download = meta?.filename || `track-${audioFileId ?? 'download'}`;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloadFlash(true);
    if (downloadFlashTimer.current) clearTimeout(downloadFlashTimer.current);
    downloadFlashTimer.current = setTimeout(() => setDownloadFlash(false), 1800);
  };

  const submitComment = async () => {
    const trimmed = comment.trim();
    if (!trimmed || commentSending) return;
    setCommentSending(true);
    await postReaction('POST', 'comment', { comment: trimmed });
    setComment('');
    setCommentSent(true);
    setCommentSending(false);
    setTimeout(() => setCommentSent(false), 2400);
  };

  if (error) {
    return (
      <main className="listen-page">
        <TopHeader />
        <Watermark />
        <div className="listen-card">
          <p className="listen-error">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="listen-page">
      <TopHeader />
      <Watermark />
      <div className="listen-card">
        <p className="listen-eyebrow">
          <span className="listen-eyebrow-dot" />
          KNKT · Demo
        </p>
        <h1 className="listen-title">
          {meta?.filename ?? <span className="listen-title-placeholder">Loading…</span>}
        </h1>

        <div className="listen-actions-row">
          <button
            type="button"
            className={`listen-share${shareCopied ? ' listen-share-copied' : ''}`}
            onClick={copyShareUrl}
            aria-label={shareCopied ? 'Copied to clipboard' : `Copy share link ${shareUrl}`}
            title="Copy share link"
          >
            <span className="listen-share-meta">
              <span className="listen-share-label">
                <Share2 size={11} strokeWidth={2.4} className="listen-share-meta-icon" />
                <span>{shareCopied ? 'Copied to clipboard' : 'Share this demo'}</span>
              </span>
              <code className="listen-share-url">{shareUrlDisplay}</code>
            </span>
            <span className="listen-share-icon-stack" aria-hidden="true">
              <Share2 size={14} strokeWidth={2.4} className="listen-share-icon listen-share-icon-default" />
              <Check size={14} strokeWidth={2.8} className="listen-share-icon listen-share-icon-success" />
            </span>
            <span className="listen-share-ring" aria-hidden="true" />
          </button>

          <button
            type="button"
            className={`listen-download${downloadFlash ? ' is-flashed' : ''}`}
            onClick={handleDownload}
            disabled={!meta?.fileUrl}
            aria-label="Download track"
            title="Download the audio file"
          >
            <Download size={13} strokeWidth={2.4} />
            <span>{downloadFlash ? 'Downloading…' : 'Download'}</span>
          </button>
        </div>

        <audio
          ref={audioRef}
          src={meta?.fileUrl ?? undefined}
          preload="metadata"
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onLoadedMetadata}
        />

        {/* Progress bar — full-width above the controls, clickable to seek. */}
        <div className="listen-progress-wrap">
          <div
            className="listen-progress-bar"
            role="slider"
            aria-label="Track position"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(currentTime)}
            onClick={seekFromEvent}
          >
            <div
              className="listen-progress-fill"
              style={{
                width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
              }}
            />
            <div
              className="listen-progress-thumb"
              style={{
                left: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
              }}
            />
          </div>
          <div className="listen-times">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Transport row — back 10, play/pause (big), forward 10. */}
        <div className="listen-controls">
          <button
            type="button"
            className="listen-skip"
            onClick={() => skip(-10)}
            disabled={!meta?.fileUrl}
            aria-label="Skip back 10 seconds"
            title="Back 10 s"
          >
            <SkipBack size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="listen-play"
            onClick={togglePlay}
            disabled={!meta?.fileUrl}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={22} strokeWidth={2} /> : <Play size={22} strokeWidth={2} />}
          </button>
          <button
            type="button"
            className="listen-skip"
            onClick={() => skip(10)}
            disabled={!meta?.fileUrl}
            aria-label="Skip forward 10 seconds"
            title="Forward 10 s"
          >
            <SkipForward size={16} strokeWidth={2} />
          </button>

          {/* Volume — mute toggle + slider. Hidden on very narrow screens
              where it would crowd transport. */}
          <div className="listen-volume">
            <button
              type="button"
              className="listen-skip listen-mute"
              onClick={toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted || volume === 0 ? <VolumeX size={15} strokeWidth={2} /> : <Volume2 size={15} strokeWidth={2} />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={muted ? 0 : volume}
              onChange={(e) => {
                const v = Number(e.target.value);
                setVolume(v);
                if (v > 0 && muted) setMuted(false);
              }}
              className="listen-volume-slider"
              aria-label="Volume"
              style={{ ['--vol' as string]: `${Math.round((muted ? 0 : volume) * 100)}%` }}
            />
          </div>
        </div>

        {/* Reactions — anonymous, no KNKT account needed. */}
        <section className="listen-reactions" aria-label="React to this demo">
          <button
            type="button"
            className={`listen-react listen-react-love${reactions.love ? ' is-on' : ''}`}
            onClick={() => toggleReaction('love')}
            aria-pressed={Boolean(reactions.love)}
            aria-label="Love this track"
            title="Love"
          >
            <Heart size={15} strokeWidth={2} fill={reactions.love ? 'currentColor' : 'none'} />
            <span>Love</span>
          </button>
          <button
            type="button"
            className={`listen-react listen-react-up${reactions.thumbs_up ? ' is-on' : ''}`}
            onClick={() => toggleReaction('thumbs_up')}
            aria-pressed={Boolean(reactions.thumbs_up)}
            aria-label="Thumbs up"
            title="Thumbs up"
          >
            <ThumbsUp size={15} strokeWidth={2} fill={reactions.thumbs_up ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            className={`listen-react listen-react-down${reactions.thumbs_down ? ' is-on' : ''}`}
            onClick={() => toggleReaction('thumbs_down')}
            aria-pressed={Boolean(reactions.thumbs_down)}
            aria-label="Thumbs down"
            title="Thumbs down"
          >
            <ThumbsDown size={15} strokeWidth={2} fill={reactions.thumbs_down ? 'currentColor' : 'none'} />
          </button>
        </section>

        {/* Optional comment — direct feedback to the artist without
            needing to reply to the email. */}
        <section className="listen-comment" aria-label="Leave a comment">
          <label className="listen-comment-label" htmlFor="listen-comment-input">
            <MessageSquare size={12} strokeWidth={2} />
            Send a quick note to the artist
          </label>
          <div className="listen-comment-row">
            <textarea
              id="listen-comment-input"
              rows={2}
              maxLength={1000}
              placeholder="Optional — 'I'm into this, drop the WAV' / 'Not for us right now' / etc."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                // Enter submits, Shift+Enter inserts a newline. Standard
                // chat-app keyboard contract — A&Rs are used to it.
                if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  submitComment();
                }
              }}
              className="listen-comment-input"
              disabled={commentSending}
            />
            <button
              type="button"
              className="listen-comment-send"
              onClick={submitComment}
              disabled={!comment.trim() || commentSending}
              aria-label="Send comment"
            >
              <Send size={13} strokeWidth={2.2} />
            </button>
          </div>
          {commentSent && (
            <p className="listen-comment-sent">Thanks — your note is on its way.</p>
          )}
        </section>

        <Copyright />

        <footer className="listen-footer">
          <Music size={11} strokeWidth={2} />
          <span>Sent via KNKT Pitch · knkt.io</span>
        </footer>
      </div>
    </main>
  );
}
